import { ComponentProps, FC, MouseEvent } from 'react'
import styles from './styles.module.scss'
import { formStateHook } from '@/hooks/useFormState'
import { EmailIcon } from '@/components/EmailForm'
import { isValidEmail, isValidLength } from '@/lib/validEmail'
import { Form } from '@/components/OpenAccount/flows/desktop/Form/Form'
import { useButtonAnalytics } from '@/hooks/useAnalytics'

export enum BusinessType {
  SOLE_TRADER = 'Sole Trader',
  LIMITED_COMPANY = 'Limited Company',
}

const createUserDetailsFormHook = ({
  onFormSubmit,
}: {
  onFormSubmit: (
    email: string,
    firstName?: string,
    businessType?: BusinessType,
    policyAgreed?: boolean,
  ) => void
}) =>
  formStateHook<{
    firstName: string
    email: string
    businessType: BusinessType
    policyAgreed: boolean
  }>({
    fields: {
      firstName: {
        type: 'text',
        applyFocus: false,
        placeholder: 'First name',
        focusClassName: styles.focus,
        required: false,
        pattern: /.+/,
        validate: (value: string | null) => {
          const trimmedFullName = value?.trim() || ''
          if (trimmedFullName.length === 1) {
            throw new Error('Name must be at least 2 characters long')
          }
          if (trimmedFullName.length > 50) {
            throw new Error('Name must be no longer than 50 characters')
          }
        },
      },
      email: {
        type: 'text',
        applyFocus: false,
        placeholder: 'Email',
        focusClassName: styles.focus,
        required: true,
        pattern: /.+/,
        extraContent: <EmailIcon />,
        validate: (email: string | null) => {
          const trimmedEmail = email?.trim() || ''
          if (!isValidLength(trimmedEmail)) {
            throw new Error('Please enter your email address')
          }
          if (!isValidEmail(trimmedEmail)) {
            throw new Error('Please enter a valid email address')
          }
        },
      },
      businessType: {
        type: 'select',
        applyFocus: false,
        placeholder: 'Business type',
        focusClassName: styles.focus,
        className: styles.businessType,
        options: ['Sole Trader', 'Limited Company'],
      },
      policyAgreed: {
        type: 'checkbox',
        label: (
          <p>
            By ticking this box, you agree to receiving marketing communications
            from us
          </p>
        ),
        className: styles.policyCheckbox,
        required: true,
        applyFocus: false,
        validate: (agreed: boolean) => {
          if (!agreed) {
            throw new Error('Confirmation needed.')
          }
        },
      },
    },
    async submit(values) {
      onFormSubmit(
        values.email,
        values.firstName || undefined,
        values.businessType || undefined,
        values.policyAgreed,
      )
    },
  })

interface LeadCaptureEmailFormProps {
  onSubmit: (
    email: string,
    firstName?: string,
    businessType?: BusinessType,
  ) => void
  onSubmitted: (values: {
    firstName: string
    email: string
    businessType: string
  }) => void
  submissionError: Error | undefined
}

export const UserDetailsForm: FC<LeadCaptureEmailFormProps> = ({
  onSubmitted,
  onSubmit,
  submissionError,
}) => {
  const useUserDetailsForm = createUserDetailsFormHook({
    onFormSubmit: onSubmit,
  })
  const userDetailsForm = useUserDetailsForm({ onSubmitted })
  const analyticsSubmit = useButtonAnalytics<HTMLButtonElement>(
    "Let's get started",
    'marketing-mailing-list-submit',
    e => userDetailsForm.onFormSubmit(e),
  )
  // Cast only the remaining props that Form expects (exclude ones we pass explicitly)
  const formProps = userDetailsForm as Omit<
    ComponentProps<typeof Form>,
    | 'onSubmitted'
    | 'onFormSubmit'
    | 'submissionError'
    | 'sendButtonText'
    | 'className'
    | 'instructions'
  >

  return (
    <Form
      onSubmitted={() => {}}
      {...formProps}
      instructions="Enter the email address you'd like to use for your Mettle account"
      onFormSubmit={e => analyticsSubmit(e as MouseEvent<HTMLButtonElement>)}
      submissionError={submissionError}
      sendButtonText="Let's get started"
      className={styles.panelContainer}
    />
  )
}
